import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Loader2, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { pizzas, focacce } from '@/data/menu';

interface OrderItemInput {
  pizzaId: string;
  pizzaName: string;
  quantity: number;
  glutenFree: boolean;
  priceCents: number;
}

interface AdminAddOrderProps {
  onOrderCreated: () => void;
}

const allProducts = [...pizzas, ...focacce];

// Generate pickup time slots from 17:40 to 21:50 in 10-minute intervals (same as customer checkout)
const generatePickupTimes = () => {
  const slots: string[] = [];
  for (let hour = 17; hour <= 21; hour++) {
    for (let minute = 0; minute < 60; minute += 10) {
      if (hour === 17 && minute < 40) continue;
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(timeStr);
    }
  }
  return slots;
};

const AdminAddOrder = ({ onOrderCreated }: AdminAddOrderProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [customerName, setCustomerName] = useState('');
  const [orderNote, setOrderNote] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [items, setItems] = useState<OrderItemInput[]>([]);
  const [selectedPizza, setSelectedPizza] = useState<string>('');
  const [selectedGlutenFree, setSelectedGlutenFree] = useState(false);
  const [pickupTime, setPickupTime] = useState<string>('');

  const resetForm = () => {
    setCustomerName('');
    setOrderNote('');
    setIsPaid(false);
    setItems([]);
    setSelectedPizza('');
    setSelectedGlutenFree(false);
    setPickupTime('');
  };

  const addItem = () => {
    if (!selectedPizza) return;
    
    const pizza = allProducts.find(p => p.id === selectedPizza);
    if (!pizza) return;

    // Check if already in list with same gluten option
    const existingIdx = items.findIndex(i => i.pizzaId === selectedPizza && i.glutenFree === selectedGlutenFree);
    if (existingIdx >= 0) {
      const updated = [...items];
      updated[existingIdx].quantity += 1;
      setItems(updated);
    } else {
      setItems([...items, {
        pizzaId: pizza.id,
        pizzaName: pizza.name,
        quantity: 1,
        glutenFree: selectedGlutenFree,
        priceCents: Math.round(pizza.price * 100),
      }]);
    }
    setSelectedPizza('');
    setSelectedGlutenFree(false);
  };


  const updateQuantity = (index: number, delta: number) => {
    const updated = [...items];
    updated[index].quantity = Math.max(1, updated[index].quantity + delta);
    setItems(updated);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!customerName.trim()) {
      toast.error('Inserisci il nome del cliente');
      return;
    }
    if (items.length === 0) {
      toast.error('Aggiungi almeno una pizza');
      return;
    }

    setLoading(true);
    try {
      const totalCents = items.reduce((sum, item) => sum + (item.priceCents * item.quantity), 0);
      
      // Generate a simple access token for manual orders
      const accessToken = `manual_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      
      // Build pickup time as ISO string if provided (in UTC)
      let pickupTimeISO: string | null = null;
      if (pickupTime) {
        const today = new Date();
        const [hours, minutes] = pickupTime.split(':').map(Number);
        // Use UTC to ensure consistent storage
        today.setUTCHours(hours, minutes, 0, 0);
        pickupTimeISO = today.toISOString();
      }

      // Create order
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert([{
          customer_name: customerName.trim(),
          customer_email: 'ordine.manuale@pizzeria.it',
          customer_phone: '-',
          customer_access_token: accessToken,
          payment_method: 'contanti' as const,
          order_note: orderNote.trim() || null,
          status: isPaid ? 'pagato' as const : 'da_pagare' as const,
          is_paid: isPaid,
          paid_at: isPaid ? new Date().toISOString() : null,
          subtotal_cents: totalCents,
          total_cents: totalCents,
          pickup_time: pickupTimeISO,
        }])
        .select('id')
        .single();

      if (orderError) throw orderError;

      // Create order items
      const orderItems = items.map(item => ({
        order_id: order.id,
        name_snapshot: item.pizzaName,
        unit_price_cents: item.priceCents,
        quantity: item.quantity,
        gluten_option: item.glutenFree ? 'senza_glutine' as const : 'con_glutine' as const,
        line_total_cents: item.priceCents * item.quantity,
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) throw itemsError;

      toast.success('Ordine creato con successo');
      resetForm();
      setOpen(false);
      onOrderCreated();
    } catch (err: any) {
      console.error('Error creating order:', err);
      toast.error('Errore nella creazione dell\'ordine');
    } finally {
      setLoading(false);
    }
  };

  const totalCents = items.reduce((sum, item) => sum + (item.priceCents * item.quantity), 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="default" size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Ordine
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Aggiungi Ordine Manuale</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {/* Customer Name */}
          <div className="space-y-2">
            <Label htmlFor="customerName">Nome Cliente *</Label>
            <Input
              id="customerName"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Es. Mario Rossi"
            />
          </div>

          {/* Pickup Time */}
          <div className="space-y-2">
            <Label>Orario Ritiro</Label>
            <Select value={pickupTime || "none"} onValueChange={(v) => setPickupTime(v === "none" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Seleziona orario...">
                  {pickupTime && (
                    <span className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {pickupTime}
                    </span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessun orario</SelectItem>
                {generatePickupTimes().map(time => (
                  <SelectItem key={time} value={time}>{time}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Add Pizza */}
          <div className="space-y-2">
            <Label>Aggiungi Pizza</Label>
            <div className="flex gap-2 items-center">
              <Select value={selectedPizza} onValueChange={setSelectedPizza}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Seleziona pizza..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Le Classiche</div>
                  {pizzas.filter(p => p.category === 'classiche').map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground mt-2">Le Specialità</div>
                  {pizzas.filter(p => p.category === 'specialita').map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground mt-2">Le Focacce</div>
                  {focacce.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer whitespace-nowrap">
                <Checkbox
                  checked={selectedGlutenFree}
                  onCheckedChange={(checked) => setSelectedGlutenFree(checked === true)}
                />
                <span className="text-green-600 font-medium">GF</span>
              </label>
              <Button type="button" onClick={addItem} disabled={!selectedPizza}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Items List */}
          {items.length > 0 && (
            <div className="space-y-2">
              <Label>Ordine</Label>
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-2 text-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => updateQuantity(idx, -1)}
                        >
                          -
                        </Button>
                        <span className="w-6 text-center">{item.quantity}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => updateQuantity(idx, 1)}
                        >
                          +
                        </Button>
                      </div>
                      <span className="truncate font-medium">{item.pizzaName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.glutenFree && (
                        <span className="text-green-600 text-xs font-medium">(GF)</span>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive"
                        onClick={() => removeItem(idx)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="border-t border-border pt-2 mt-2 flex justify-between font-semibold">
                  <span>Totale</span>
                  <span className="text-primary">€{(totalCents / 100).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Order Note */}
          <div className="space-y-2">
            <Label htmlFor="orderNote">Note (opzionale)</Label>
            <Textarea
              id="orderNote"
              value={orderNote}
              onChange={(e) => setOrderNote(e.target.value)}
              placeholder="Es. senza cipolla, extra piccante..."
              rows={2}
            />
          </div>

          {/* Payment Status */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="isPaid"
              checked={isPaid}
              onCheckedChange={(checked) => setIsPaid(checked === true)}
            />
            <Label htmlFor="isPaid" className="cursor-pointer">
              Pagato al momento
            </Label>
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={loading || !customerName.trim() || items.length === 0}
            className="w-full"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Crea Ordine
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminAddOrder;
